import { useState } from "react";
import { api } from "../api";
import { ApiError } from "../api/types";
import { Button, SelectField, TextField } from "../compose/controls";
import { Dialog } from "../compose/Dialog";
import { Copy, Link2, UserPlus } from "../compose/Icon";
import { useToast } from "../compose/Toast";

const ROLES = [
  { value: "viewer", label: "Viewer" },
  { value: "commenter", label: "Commenter" },
  { value: "editor", label: "Editor" },
  { value: "reviewer", label: "Reviewer" },
  { value: "author", label: "Author" },
  { value: "workspace-admin", label: "Workspace admin" },
];

/**
 * Invite person — staff action (design screens §people). Calls
 * POST /workspaces/:slug/people and reveals the generated set-password link to
 * copy and hand off. The link is a real, working /set-password?token=… route.
 */
export function InviteDialog({
  slug,
  onClose,
  onInvited,
}: {
  slug: string;
  onClose: () => void;
  onInvited: () => void;
}) {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("commenter");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const fullUrl = inviteUrl
    ? `${window.location.origin}${inviteUrl}`
    : "";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError("Enter an email address.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.invitePerson(slug, { email: email.trim(), role });
      setInviteUrl(res.invite_url);
      onInvited();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not create the invite.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Invite a person"
      subtitle="Create a workspace invite and share the set-password link."
      onClose={onClose}
      testid="invite-dialog"
    >
      {inviteUrl ? (
        <div>
          <div className="invite-result" data-testid="invite-result">
            <div className="invite-result__label">
              <Link2 size={15} strokeWidth={1.5} />
              Set-password link for {email}
            </div>
            <div className="invite-result__link">
              <span className="invite-result__url" data-testid="invite-url">
                {fullUrl}
              </span>
              <Button
                type="button"
                variant="secondary"
                data-testid="copy-invite"
                icon={<Copy size={15} strokeWidth={1.5} />}
                onClick={() => {
                  void navigator.clipboard?.writeText(fullUrl).catch(() => {});
                  toast.show("Invite link copied");
                }}
              >
                Copy
              </Button>
            </div>
          </div>
          <div className="dialog__actions">
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit}>
          {error ? (
            <div className="auth__error" role="alert">
              {error}
            </div>
          ) : null}
          <TextField
            label="Email"
            type="email"
            placeholder="person@company.com"
            value={email}
            data-testid="invite-email"
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
          <SelectField
            label="Role"
            options={ROLES}
            value={role}
            data-testid="invite-role"
            onChange={(e) => setRole(e.target.value)}
          />
          <div className="dialog__actions">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={busy}
              data-testid="invite-submit"
              icon={<UserPlus size={15} strokeWidth={1.5} />}
            >
              {busy ? "Creating…" : "Create invite"}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
